import { mergeAttributes } from '@tiptap/core'
import { Mention } from '@tiptap/extension-mention'
import { PluginKey } from '@tiptap/pm/state'
import Suggestion from '@tiptap/suggestion'
import { getUserMentionPopup } from '../components/UserMentionPopup'

export interface UserMentionItem {
  login: string
  name: string
  firstname?: string
  lastname?: string
}

interface Options {
  url: string
}

// Matches @login preceded by whitespace or start-of-string.
// Negative lookbehind avoids matching emails (word char before @).
const USER_MENTION_RE = /(?<![\w.])@([\w-]+)(?=[\s.,;:!?)\]]|$)/gm

export const UserMention = Mention.extend<Options>({
  name: 'userMention',

  addOptions() {
    return {
      ...this.parent?.(),
      url: '',
    }
  },

  addAttributes() {
    return {
      // `id` holds the login (Mention's canonical identifier attribute)
      id: {
        default: null,
        parseHTML: el => el.getAttribute('data-user-login') || '',
        renderHTML: attrs => ({ 'data-user-login': attrs.id }),
      },
      label: {
        default: null,
        parseHTML: el => el.getAttribute('data-user-label') || '',
        renderHTML: attrs => ({ 'data-user-label': attrs.label }),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'span[data-user-mention]' }]
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes({ 'data-user-mention': '', 'class': 'user-mention-node' }, HTMLAttributes),
      `@${node.attrs.id}`,
    ]
  },

  renderMarkdown(node) {
    return `@${node.attrs!.id}`
  },

  markdownTokenName: 'userMention',

  markdownTokenizer: {
    name: 'userMention',
    level: 'inline',
    start: src => src.indexOf('@'),
    tokenize: (src) => {
      USER_MENTION_RE.lastIndex = 0
      const m = USER_MENTION_RE.exec(src)
      if (!m)
        return
      return { type: 'userMention', raw: m[0], login: m[1], tokens: [] } as any
    },
  },

  parseMarkdown(token) {
    const login = (token as any).login ?? ''
    return { type: 'userMention', attrs: { id: login, label: login } }
  },

  addProseMirrorPlugins() {
    const baseUrl = this.options.url
    let abortCtrl: AbortController | undefined

    return [
      Suggestion({
        pluginKey: new PluginKey('userMentionSuggestion'),
        editor: this.editor,
        char: '@',
        allowSpaces: false,

        items: async ({ query }) => {
          abortCtrl?.abort()
          abortCtrl = new AbortController()
          try {
            const res = await fetch(baseUrl + encodeURIComponent(query), { signal: abortCtrl.signal })
            if (!res.ok)
              return []
            return res.json() as Promise<UserMentionItem[]>
          }
          catch {
            return []
          }
          finally {
            abortCtrl = undefined
          }
        },

        command: ({ editor, range, props }) => {
          const item = props as UserMentionItem
          editor.chain().focus().insertContentAt(range, [
            { type: 'userMention', attrs: { id: item.login, label: item.name } },
          ]).run()
        },

        render: () => ({
          onStart(props) {
            getUserMentionPopup()?.show(props.items, props.clientRect!, props.command)
          },
          onUpdate(props) {
            getUserMentionPopup()?.update(props.items, props.clientRect!)
          },
          onKeyDown({ event }) {
            return getUserMentionPopup()?.handleKeyDown(event) ?? false
          },
          onExit() {
            getUserMentionPopup()?.hide()
          },
        }),
      }),
    ]
  },
})
